/**
 * /wireline-slickline/lines — the tenant's lines
 * (GET /wireline-slickline/lines, fieldiq-api#1336, WL-1).
 *
 * Full-width register; a row opens the line on its own page, where its event
 * log and folded state live. The line-type chips filter server-side
 * (`lineType`) and live in the URL.
 *
 * EVERY STATE FIGURE IS THE API'S. The length on the drum, the pull-test and
 * inspection status are read off `line.state` (folded in fieldiq-api
 * lib/wl-line.ts) and rendered; nothing here folds the event log. On drum is
 * the API's `lengthOnDrum` with its `lengthUnit` (m or ft), never converted
 * here. Absent is `—`.
 *
 * The band counts are a tally of the rows the unfiltered list returned.
 *
 * New Line is WL_GEAR_WRITE (admin, office_staff, supervisor).
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Cable, Plus, XCircle } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWlLines, useWlMeta } from '../../hooks/useWirelineSlickline';
import { queryLoadFailure } from '../../lib/load-failure';
import { canWriteWlGear, WL_RETIRED_SKIN, wlCatalogLabel, wlDueSkin, wlLineLength } from '../../lib/wireline-slickline';
import type { WlLine } from '../../types/wireline-slickline';
import { useOpenRecord } from '../downhole/recordPage';
import { WL_EYEBROW, WlBadge, WlChip, WlChipRow, WlEmpty, WlPanel, WlRegister, type WlColumn } from './WlBits';

const railOf = (l: WlLine) => (l.state?.retired ? WL_RETIRED_SKIN.rail : wlDueSkin(l.state?.pullTest?.status).rail);

export default function WlLinesScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const lineType = searchParams.get('lineType') ?? '';
  const setLineType = (next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('lineType', next);
    else p.delete('lineType');
    setSearchParams(p, { replace: true });
  };
  const catalog = useWlMeta().data?.data ?? null;
  const listQuery = useWlLines(lineType ? { lineType } : undefined);
  const allQuery = useWlLines();
  const lines = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (l: WlLine) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canWriteWlGear(user?.role);

  const typeLabel = (l: WlLine) => l.lineTypeLabel ?? wlCatalogLabel(catalog?.lineTypes, l.lineType);

  const columns: WlColumn<WlLine>[] = [
    {
      key: 'line',
      header: 'Line',
      render: (l) => (
        <>
          <p className="font-extrabold text-white">{l.lineNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{typeLabel(l)}</p>
        </>
      ),
    },
    {
      key: 'make',
      header: 'Make / serial',
      render: (l) => (
        <>
          <p>{l.manufacturer || '—'}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{l.serialNumber || '—'}</p>
        </>
      ),
    },
    { key: 'drum', header: 'On drum', align: 'right', render: (l) => <span className="font-mono text-sm">{wlLineLength(l.state?.lengthOnDrum, l.state?.lengthUnit)}</span> },
    { key: 'pull', header: 'Pull test', render: (l) => <WlBadge skin={wlDueSkin(l.state?.pullTest?.status)} value={l.state?.pullTest?.status} /> },
    { key: 'inspection', header: 'Inspection', render: (l) => <WlBadge skin={wlDueSkin(l.state?.inspection?.status)} value={l.state?.inspection?.status} /> },
    {
      key: 'status',
      header: 'Status',
      render: (l) => (l.state?.retired ? <WlBadge skin={WL_RETIRED_SKIN} value="retired" /> : l.state ? (l.state.spooled ? 'Spooled' : 'Not spooled') : '—'),
    },
  ];

  return (
    <div data-testid="wl-lines-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Lines"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/wireline-slickline/lines/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New line
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Cable className="size-4" aria-hidden="true" />, label: 'Lines', sub: 'Registered', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Pull test',
            sub: 'Overdue or failed',
            tone: 'accent',
            loading: countsLoading,
            value: countOf((l) => l.state?.pullTest?.status === 'overdue' || l.state?.pullTest?.status === 'failed'),
          },
          { icon: <XCircle className="size-4" aria-hidden="true" />, label: 'Retired', tone: 'neutral', loading: countsLoading, value: countOf((l) => !!l.state?.retired) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlChipRow label="Filter by line type">
            <WlChip label="All" active={!lineType} onClick={() => setLineType('')} count={countsLoading ? undefined : countOf(() => true)} />
            {(catalog?.lineTypes ?? []).map((t) => (
              <WlChip
                key={t.value}
                label={t.label}
                active={lineType === t.value}
                onClick={() => setLineType(t.value)}
                count={countsLoading ? undefined : countOf((l) => l.lineType === t.value)}
              />
            ))}
          </WlChipRow>
          <WlPanel
            eyebrow="Line register"
            title="Lines"
            testId="wl-line-register"
            footer={!listQuery.isLoading && !queryLoadFailure(listQuery) && lines.length > 0 ? `${lines.length} line${lines.length === 1 ? '' : 's'}` : undefined}
          >
            <WlRegister
              query={listQuery}
              rows={lines}
              columns={columns}
              rail={railOf}
              onOpen={(l) => openRecord(`/wireline-slickline/lines/${l.id}`)}
              subject="lines"
              testId="wl-line"
              minWidth="min-w-[860px]"
              empty={lineType ? <WlEmpty>No line of this type.</WlEmpty> : <WlEmpty>There are no lines yet.</WlEmpty>}
              card={(l) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{l.lineNumber || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{typeLabel(l)}</p>
                    </div>
                    {l.state?.retired && <WlBadge skin={WL_RETIRED_SKIN} value="retired" />}
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    On drum {wlLineLength(l.state?.lengthOnDrum, l.state?.lengthUnit)} · Pull test {wlDueSkin(l.state?.pullTest?.status).label} · Inspection{' '}
                    {wlDueSkin(l.state?.inspection?.status).label}
                  </p>
                </>
              )}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
